import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { DropdownButton, TabbedDropdown } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { DropdownSmartTabs } from "app/pages/sales-intelligence/common-components/dropdown/DropdownTabs/DropdownTabs";
import { FilterConditionDialog } from "app/pages/sales-intelligence/common-components/filters-panel/components/FilterConditionDialog/FilterConditionDialog";
import DropdownTabPanel from "app/pages/sales-intelligence/common-components/form-fields/DropdownTabPanel/DropdownTabPanel";
import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import TechnologyChipsGroup from "app/pages/sales-intelligence/modules/technology/components/TechnologyChipsGroup/TechnologyChipsGroup";
import useDropdownTabbedSearch from "app/pages/sales-intelligence/modules/technology/hook/useDropdownTabbedSearch";
import type { DisabledDropdownTechnologyValues } from "app/pages/sales-intelligence/modules/technology/types/dropdown";
import type {
    GroupedTechnologies,
    TechnologiesFilterDto,
} from "app/pages/sales-intelligence/modules/technology/types/technologies";
import {
    getDisabledTechnologyValues,
    getTechnologyTabs,
    hasSameTechnologyObject,
    updateTechnologyItem,
} from "app/pages/sales-intelligence/modules/technology/utils/technologies";
import { type InclusionOption, inclusionOptionMap } from "app/pages/sales-intelligence/types/commonMaps";

import InclusionTechnologyRadioButtons from "../../InclusionTechnologyRadioButtons/InclusionTechnologyRadioButtons";

import {
    StyledContainerWithChips,
    StyledDDContent,
    StyledDropdownButtonContainer,
    StyledTabbedDropdownWrapper,
} from "./styles";

const INIT_TECHNOLOGY_MODAL_VALUE: TechnologiesFilterDto = {
    inclusion: inclusionOptionMap.includeOnly,
    categories: [],
    parentCategories: [],
    technologies: [],
};

interface ITechnologyModalProps {
    isOpen: boolean;
    onClose: () => void;
    selected: TechnologiesFilterDto | null;
    onCancel: () => void;
    onApply: (item: TechnologiesFilterDto, selected?: TechnologiesFilterDto | null) => void;
    options: GroupedTechnologies;
    value: TechnologiesFilterDto[] | null;
}

export const TechnologyModal: FC<ITechnologyModalProps> = ({
    isOpen,
    options,
    value,
    onClose,
    onCancel,
    onApply,
    selected,
}) => {
    const [isOpenDD, setIsOpenDD] = useState(false);

    const [technologyItem, setTechnologyItem] = useState<TechnologiesFilterDto>(INIT_TECHNOLOGY_MODAL_VALUE);

    const dropdownTitle = translate("si.lead_gen_filters.filters.technologies.dropdown.title");

    const hasChips =
        technologyItem.technologies.length > 0 ||
        technologyItem.categories.length > 0 ||
        technologyItem.parentCategories.length > 0;

    const initValue = () => {
        setTechnologyItem(INIT_TECHNOLOGY_MODAL_VALUE);
    };

    const handleSetInclusion = (inclusionOption: InclusionOption) => {
        const technology = { ...technologyItem, inclusion: inclusionOption };
        setTechnologyItem(technology);
    };

    const handleCancel = () => {
        onCancel();
        initValue();
    };

    const handleOnApply = () => {
        onApply(technologyItem, selected);
        initValue();
    };

    const isApplyDisabled = () => {
        const { categories, parentCategories, technologies } = technologyItem;
        return (
            (categories.length === 0 && technologies.length === 0 && parentCategories.length === 0) ||
            hasSameTechnologyObject(technologyItem, selected)
        );
    };

    const handleOnToggleClick = (open: boolean) => {
        setIsOpenDD(open);
    };

    const handleOnItemClick = (item: { name: string; secondaryName?: string; type?: string }) => {
        setTechnologyItem((prev) => updateTechnologyItem(prev, item, options));
    };

    useEffect(() => {
        if (isOpen && selected) {
            setTechnologyItem(selected);
        }

        if (!isOpen) {
            initValue();
        }
    }, [isOpen, selected]);

    const isEnabledExclude = useMemo((): boolean => {
        return (
            (selected && selected?.inclusion === inclusionOptionMap.excludeOnly) ||
            !value?.find((item) => item.inclusion === inclusionOptionMap.excludeOnly)
        );
    }, [selected, value]);

    const tabs = useMemo(() => {
        const disabledValues: DisabledDropdownTechnologyValues = getDisabledTechnologyValues(value ?? [], selected);

        return getTechnologyTabs(options, disabledValues, technologyItem);
    }, [options, technologyItem, value, selected]);

    const { tabsWithSortedItems, searchTerm, searchProps } = useDropdownTabbedSearch(
        {
            tabs,
            searchPlaceholder: translate("si.technology.modal.dropdown.placeholder"),
        },
        { shouldProcessItems: isOpen },
    );

    return (
        <FilterConditionDialog
            isOpen={isOpen}
            onRequestClose={onClose}
            dataAutomation="technology-modal"
            title={<I18n translatable={{ key: "si.technology.modal.title" }} />}
            description={<I18n translatable={{ key: "si.technology.modal.subtitle" }} />}
            onCancel={handleCancel}
            onApply={handleOnApply}
            isApplyDisabled={isApplyDisabled()}
        >
            <InclusionTechnologyRadioButtons
                isExcludeAvailable={isEnabledExclude}
                selectedInclusion={technologyItem.inclusion}
                onSelect={handleSetInclusion}
            />
            <StyledContainerWithChips hasChips={hasChips}>
                <TechnologyChipsGroup
                    item={technologyItem}
                    onChangeTechnology={setTechnologyItem}
                />
                <StyledDDContent>
                    <StyledTabbedDropdownWrapper>
                        <TabbedDropdown
                            dropdownTitle={dropdownTitle}
                            onItemClick={handleOnItemClick}
                            isOpen={isOpenDD}
                            searchTerm={searchTerm}
                            tabs={tabsWithSortedItems}
                            buttonComponent={() => (
                                <StyledDropdownButtonContainer>
                                    <DropdownButton>{dropdownTitle}</DropdownButton>
                                </StyledDropdownButtonContainer>
                            )}
                            panelComponent={DropdownTabPanel}
                            tabsView={DropdownSmartTabs}
                            onToggle={handleOnToggleClick}
                            searchProps={searchProps}
                            className="technology-category-dd"
                            height={240}
                            fullWidth
                        />
                    </StyledTabbedDropdownWrapper>
                </StyledDDContent>
            </StyledContainerWithChips>
        </FilterConditionDialog>
    );
};
